/**
 * App settings — edit metadata + hard-delete. Owner-only.
 *
 * Backed by:
 *   - PATCH  /api/apps/:slug
 *   - DELETE /api/apps/:slug  (hard-delete; requires { confirm_slug })
 *
 * Visibility lives on Access, not here. Slug is intentionally read-only —
 * changing it would break every existing URL the app has.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, apiMessage } from '../api/client.js';
import { AppTabs } from '../components/AppTabs.js';
import { ManagerOnlyPage } from '../components/ManagerOnly.js';
import { IconAlert, IconTrash } from '../components/Icons.js';
import {
  Banner,
  Button,
  Card,
  CardHeader,
  Dialog,
  Field,
  PageHeader,
  PageSkeleton,
  inputClass,
} from '../components/ui.js';
import { useAppPage } from '../lib/useAppPage.js';
import { runAction, type ActionMessage } from '../lib/runAction.js';

export function Settings() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { app, loading, error, setApp } = useAppPage(slug);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  // Seed the form once per app — don't re-seed on reload, which would
  // clobber the user's in-flight edits.
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    if (app && seededFor.current !== app.id) {
      seededFor.current = app.id;
      setName(app.name);
      setDescription(app.description ?? '');
    }
  }, [app]);
  const [busy, setBusy] = useState(false);
  const [actionMessage, setActionMessage] = useState<ActionMessage | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  if (loading) return <PageSkeleton rows={3} />;
  if (error) return <Banner tone="error">{error}</Banner>;
  if (!app) return null;

  if (!app.is_manager) {
    return <ManagerOnlyPage app={app} title="Settings" description="App settings." />;
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!slug || !app) return;
    const patch: Record<string, unknown> = {};
    if (name !== app.name) patch['name'] = name;
    const newDescription = description.length > 0 ? description : null;
    if (newDescription !== (app.description ?? null)) patch['description'] = newDescription;
    if (Object.keys(patch).length === 0) {
      setActionMessage({ tone: 'info', text: 'Nothing to save.' });
      return;
    }
    await runAction({
      do: () => api.patch(`/api/apps/${encodeURIComponent(slug)}`, patch),
      onSuccess: () => 'Saved.',
      errorPrefix: "Couldn't save",
      setBusy,
      setMessage: setActionMessage,
    });
    // Update the in-memory app row so the displayed values match what
    // we just sent — avoids the wasted GET that `reload()` would do.
    setApp((prev) => ({
      ...prev,
      name,
      description: newDescription,
      updated_at: Math.floor(Date.now() / 1000),
    }));
  }

  async function handleConfirmedDelete() {
    if (!slug) return;
    setBusy(true);
    setActionMessage(null);
    try {
      await api.delete(`/api/apps/${encodeURIComponent(slug)}`, { confirm_slug: slug });
      navigate('/', { replace: true });
    } catch (err) {
      setActionMessage({ tone: 'error', text: `Couldn't delete: ${apiMessage(err)}` });
      setBusy(false);
      setDeleteOpen(false);
    }
  }

  return (
    <div className="animate-fade-in">
      <PageHeader
        back={{ to: `/apps/${app.slug}`, label: app.name }}
        title="Settings"
        description="Owner-only. Slug is fixed; visit Access to manage who can reach the app."
      />

      <div className="grid grid-cols-12 gap-6">
        <AppTabs
          slug={app.slug}
          isManager={app.is_manager}
          hasSchedules={app.has_schedules ?? false}
        />
        <main className="col-span-12 lg:col-span-9 xl:col-span-10 min-w-0 space-y-5">
          {actionMessage && (
            <Banner tone={actionMessage.tone} onDismiss={() => setActionMessage(null)}>
              {actionMessage.text}
            </Banner>
          )}

          <form onSubmit={(e) => void save(e)}>
            <Card>
              <CardHeader
                title="Details"
                description="Name and description shown in the catalogue."
              />
              <div className="px-5 py-5 space-y-4">
                <Field label="Slug" hint="Fixed — changing it would break URLs.">
                  <input
                    type="text"
                    value={app.slug}
                    disabled
                    className={`${inputClass} font-mono !bg-surface-2 !text-muted`}
                  />
                </Field>
                <Field label="Name" required>
                  <input
                    type="text"
                    required
                    maxLength={200}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={inputClass}
                  />
                </Field>
                <Field label="Description" hint="A one-line description shown to teammates.">
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    placeholder="What does this app do?"
                    className={`${inputClass} resize-y`}
                  />
                </Field>
              </div>
              <div className="px-5 py-3 border-t border-line bg-surface-2/40 flex justify-end rounded-b-xl">
                <Button type="submit" variant="primary" size="md" loading={busy} disabled={busy}>
                  Save changes
                </Button>
              </div>
            </Card>
          </form>

          <Card className="!border-danger/30">
            <CardHeader
              title={
                <span className="inline-flex items-center gap-1.5 text-danger">
                  <IconAlert size={14} />
                  Danger zone
                </span>
              }
              description="Permanent. There is no undo."
            />
            <div className="px-5 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-sm text-muted">
                Delete the Worker, both databases, every uploaded file, and every published version.
                This cannot be undone.
              </p>
              <Button
                variant="danger"
                size="md"
                onClick={() => setDeleteOpen(true)}
                disabled={busy}
              >
                <IconTrash size={13} />
                Delete app
              </Button>
            </div>
          </Card>
        </main>
      </div>

      {deleteOpen && (
        <DeleteConfirmModal
          appName={app.name}
          slug={app.slug}
          busy={busy}
          onCancel={() => setDeleteOpen(false)}
          onConfirm={handleConfirmedDelete}
        />
      )}
    </div>
  );
}

function DeleteConfirmModal({
  appName,
  slug,
  busy,
  onCancel,
  onConfirm,
}: {
  appName: string;
  slug: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [typed, setTyped] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onCancel();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  const matches = typed === slug;

  return (
    <Dialog
      title={
        <span className="inline-flex items-center gap-2 text-danger">
          <IconAlert size={18} />
          Delete &quot;{appName}&quot;?
        </span>
      }
      onClose={onCancel}
      busy={busy}
      footer={
        <>
          <Button variant="secondary" size="md" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="danger"
            size="md"
            onClick={onConfirm}
            loading={busy}
            disabled={!matches || busy}
          >
            Delete permanently
          </Button>
        </>
      }
    >
      <p>
        This permanently deletes the app and{' '}
        <span className="font-semibold text-ink">all of its data</span>: the Worker, both databases
        (preview + live), every uploaded asset, every source archive, and every published version.{' '}
        <span className="font-semibold text-ink">This cannot be undone.</span>
      </p>
      <p className="text-muted">
        To confirm, type the slug{' '}
        <code className="font-mono bg-surface-2 px-1.5 py-0.5 rounded text-ink">{slug}</code> below.
      </p>
      <input
        ref={inputRef}
        type="text"
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        autoComplete="off"
        spellCheck={false}
        placeholder={slug}
        disabled={busy}
        className={`${inputClass} font-mono`}
      />
    </Dialog>
  );
}
